import axiosInstance from './axiosInstance';
import { API_ENDPOINTS } from '../config/api';
import {
  Bird,
  BirdProcurement,
  BirdLog,
  BirdProcurementAssignee,
  EggProduction,
  EggSale,
  EggPrice,
  EggStock,
  Customer,
  CustomerStats,
  OutstandingCustomersResponse,
  FinancialCycle,
  Expenditure,
  ExpenditureCategory,
  ExpenditureGroup,
  Mortality,
  Decommission,
  DecommissionStats,
  FeedType,
  FeedBrand,
  FeedStock,
  FeedProcurement,
  FeedAssignment,
  Request,
  DashboardOverview,
  DashboardMetrics,
  AlertsResponse,
  Stats,
  BirdStats,
  MortalityStats,
  PaginatedResponse,
  ApiResponse,
  User,
  Permission,
  UserPermissions,
  PaginationMeta,
} from '../types';

// Dashboard Service
export const dashboardService = {
  getOverview: async (): Promise<DashboardOverview> => {
    const response = await axiosInstance.get<ApiResponse<DashboardOverview>>(
      API_ENDPOINTS.DASHBOARD.OVERVIEW
    );
    return response.data.data;
  },

  getMetrics: async (): Promise<DashboardMetrics> => {
    const response = await axiosInstance.get<ApiResponse<DashboardMetrics>>(
      API_ENDPOINTS.DASHBOARD.METRICS
    );
    return response.data.data;
  },

  getAlerts: async (): Promise<AlertsResponse> => {
    const response = await axiosInstance.get<ApiResponse<AlertsResponse>>(
      API_ENDPOINTS.DASHBOARD.ALERTS
    );
    return response.data.data;
  },

  getBirdOverview: async (birdId: number): Promise<any> => {
    const response = await axiosInstance.get(API_ENDPOINTS.DASHBOARD.BIRD_OVERVIEW(birdId));
    return response.data.data;
  },

  getDailyOverview: async (): Promise<any> => {
    const response = await axiosInstance.get(API_ENDPOINTS.DASHBOARD.DAILY_OVERVIEW);
    return response.data.data;
  },
};

// Birds Service
export const birdsService = {
  getAll: async (params?: any): Promise<PaginatedResponse<Bird>> => {
    const response = await axiosInstance.get<ApiResponse<PaginatedResponse<Bird>>>(
      API_ENDPOINTS.BIRDS.LIST,
      { params }
    );
    return response.data.data;
  },
  getTypes: async (): Promise<string[]> => {
    const response = await axiosInstance.get<ApiResponse<string[]>>(API_ENDPOINTS.BIRDS.TYPES);
    return response.data.data;
  },
  getById: async (id: number): Promise<Bird> => {
    const response = await axiosInstance.get<ApiResponse<Bird>>(API_ENDPOINTS.BIRDS.DETAIL(id));
    return response.data.data;
  },

  create: async (data: Partial<Bird>): Promise<Bird> => {
    const response = await axiosInstance.post<ApiResponse<Bird>>(API_ENDPOINTS.BIRDS.CREATE, data);
    return response.data.data;
  },

  update: async (id: number, data: Partial<Bird>): Promise<Bird> => {
    const response = await axiosInstance.put<ApiResponse<Bird>>(
      API_ENDPOINTS.BIRDS.DETAIL(id),
      data
    );
    return response.data.data;
  },

  delete: async (id: number): Promise<void> => {
    await axiosInstance.delete(API_ENDPOINTS.BIRDS.DETAIL(id));
  },

  getStats: async (): Promise<BirdStats> => {
    const response = await axiosInstance.get<ApiResponse<BirdStats>>(API_ENDPOINTS.BIRDS.STATS);
    return response.data.data;
  },

  getQuantities: async (): Promise<any> => {
    const response = await axiosInstance.get(API_ENDPOINTS.BIRDS.QUANTITIES);
    return response.data.data;
  },

  startLaying: async (id: number, data: any): Promise<Bird> => {
    const response = await axiosInstance.patch<ApiResponse<Bird>>(
      API_ENDPOINTS.BIRDS.START_LAYING(id),
      data
    );
    return response.data.data;
  },

  getLogs: async (id: number): Promise<BirdLog[]> => {
    const response = await axiosInstance.get<ApiResponse<{ bird: any; logs: BirdLog[] }>>(
      API_ENDPOINTS.BIRDS.LOGS(id)
    );
    return response.data.data.logs;
  },

  updateFeedInfo: async (
    id: number,
    data: { feed_type_id: number; feed_brand_id: number }
  ): Promise<Bird> => {
    const response = await axiosInstance.patch<ApiResponse<Bird>>(
      API_ENDPOINTS.BIRDS.UPDATE_FEED_INFO(id),
      data
    );
    return response.data.data;
  },

  calculateFeedEstimate: async (id: number): Promise<any> => {
    const response = await axiosInstance.post<ApiResponse<any>>(API_ENDPOINTS.BIRDS.CALCULATE(id));
    return response.data.data;
  },
};

// Bird Procurements Service
export const birdBatchesService = {
  getAll: async (params?: any): Promise<any> => {
    const response = await axiosInstance.get(API_ENDPOINTS.BIRD_BATCHES.LIST, { params });
    return response.data.data;
  },
  getById: async (id: number): Promise<any> => {
    const response = await axiosInstance.get(API_ENDPOINTS.BIRD_BATCHES.DETAIL(id));
    return response.data.data;
  },
  update: async (id: number, data: any): Promise<any> => {
    const response = await axiosInstance.patch(API_ENDPOINTS.BIRD_BATCHES.UPDATE(id), data);
    return response.data.data;
  },
};

export const birdProcurementsService = {
  getAll: async (params?: any): Promise<PaginatedResponse<BirdProcurement>> => {
    const response = await axiosInstance.get<ApiResponse<any>>(
      API_ENDPOINTS.BIRD_PROCUREMENTS.LIST,
      { params }
    );
    // Backend returns Laravel paginated format, transform to our format
    const paginatedData = response.data.data;
    return {
      data: paginatedData.data,
      meta: {
        current_page: paginatedData.current_page,
        last_page: paginatedData.last_page,
        per_page: paginatedData.per_page,
        total: paginatedData.total,
        from: paginatedData.from,
        to: paginatedData.to,
      },
    };
  },
  getSuppliers: async (): Promise<any[]> => {
    const response = await axiosInstance.get<ApiResponse<any>>(
      API_ENDPOINTS.BIRD_PROCUREMENTS.SUPPLIERS
    );
    return response.data.data;
  },
  createSupplier: async (name: string): Promise<any> => {
    const response = await axiosInstance.post<ApiResponse<any>>('/feed/suppliers', { name });
    return response.data.data;
  },
  getById: async (id: number): Promise<BirdProcurement> => {
    const response = await axiosInstance.get<ApiResponse<BirdProcurement>>(
      API_ENDPOINTS.BIRD_PROCUREMENTS.DETAIL(id)
    );
    return response.data.data;
  },

  create: async (data: Partial<BirdProcurement>): Promise<BirdProcurement> => {
    const response = await axiosInstance.post<ApiResponse<BirdProcurement>>(
      API_ENDPOINTS.BIRD_PROCUREMENTS.CREATE,
      data
    );
    return response.data.data;
  },

  update: async (id: number, data: Partial<BirdProcurement>): Promise<BirdProcurement> => {
    const response = await axiosInstance.put<ApiResponse<BirdProcurement>>(
      API_ENDPOINTS.BIRD_PROCUREMENTS.DETAIL(id),
      data
    );
    return response.data.data;
  },

  delete: async (id: number): Promise<void> => {
    await axiosInstance.delete(API_ENDPOINTS.BIRD_PROCUREMENTS.DETAIL(id));
  },

  markAsDelivered: async (
    id: number,
    data: {
      birds: { bird_id: number; quantity_delivered: number; date_delivered: string }[];
    }
  ): Promise<BirdProcurement> => {
    const response = await axiosInstance.post<ApiResponse<BirdProcurement>>(
      API_ENDPOINTS.BIRD_PROCUREMENTS.MARK_DELIVERED(id),
      data
    );
    return response.data.data;
  },

  getStats: async (): Promise<Stats> => {
    const response = await axiosInstance.get<ApiResponse<Stats>>(
      API_ENDPOINTS.BIRD_PROCUREMENTS.STATS
    );
    return response.data.data;
  },
};

// Bird Logs Service
export const birdLogsService = {
  getAll: async (params?: any): Promise<PaginatedResponse<BirdLog>> => {
    const response = await axiosInstance.get<ApiResponse<any>>(API_ENDPOINTS.BIRD_LOGS.LIST, {
      params,
    });
    // Transform Laravel pagination to our format
    const laravelData = response.data.data;
    return {
      data: laravelData.data,
      meta: {
        current_page: laravelData.current_page,
        from: laravelData.from,
        last_page: laravelData.last_page,
        per_page: laravelData.per_page,
        to: laravelData.to,
        total: laravelData.total,
      },
    };
  },

  getById: async (id: number): Promise<BirdLog> => {
    const response = await axiosInstance.get<ApiResponse<BirdLog>>(
      API_ENDPOINTS.BIRD_LOGS.DETAIL(id)
    );
    return response.data.data;
  },

  create: async (data: Partial<BirdLog>): Promise<BirdLog> => {
    const response = await axiosInstance.post<ApiResponse<BirdLog>>(
      API_ENDPOINTS.BIRD_LOGS.CREATE,
      data
    );
    return response.data.data;
  },

  delete: async (id: number): Promise<void> => {
    await axiosInstance.delete(API_ENDPOINTS.BIRD_LOGS.DELETE(id));
  },
};

// Bird Assignees Service
export const batchEmployeeService = {
  getForBatch: async (batchId: number) => {
    const response = await axiosInstance.get<ApiResponse<any>>(
      API_ENDPOINTS.BIRD_BATCHES.EMPLOYEES(batchId)
    );
    return response.data.data;
  },

  assign: async (batchId: number, data: { employee_ids: number[]; assigned_at?: string }) => {
    const response = await axiosInstance.post<ApiResponse<any>>(
      API_ENDPOINTS.BIRD_BATCHES.EMPLOYEES(batchId),
      data
    );
    return response.data;
  },

  release: async (batchId: number, employeeId: number): Promise<void> => {
    await axiosInstance.delete(API_ENDPOINTS.BIRD_BATCHES.RELEASE_EMPLOYEE(batchId, employeeId));
  },
};

export const birdProcurementAssigneesService = {
  getAll: async (): Promise<BirdProcurementAssignee[]> => {
    const response = await axiosInstance.get<ApiResponse<BirdProcurementAssignee[]>>(
      API_ENDPOINTS.BIRD_PROCUREMENT_ASSIGNEES.LIST
    );
    return response.data.data;
  },

  create: async (data: {
    procurement_ids: number[];
    assignee_id: number;
  }): Promise<BirdProcurementAssignee[]> => {
    const response = await axiosInstance.post<ApiResponse<BirdProcurementAssignee[]>>(
      API_ENDPOINTS.BIRD_PROCUREMENT_ASSIGNEES.CREATE,
      data
    );
    return response.data.data;
  },

  delete: async (id: number): Promise<void> => {
    await axiosInstance.delete(API_ENDPOINTS.BIRD_PROCUREMENT_ASSIGNEES.DELETE(id));
  },

  getStats: async (): Promise<any> => {
    const response = await axiosInstance.get<ApiResponse<any>>(
      API_ENDPOINTS.BIRD_PROCUREMENT_ASSIGNEES.STATS
    );
    return response.data.data;
  },
};

// Egg Production Service
export const eggProductionService = {
  getAll: async (params?: any): Promise<PaginatedResponse<EggProduction>> => {
    const response = await axiosInstance.get<ApiResponse<PaginatedResponse<EggProduction>>>(
      API_ENDPOINTS.EGG_PRODUCTIONS.LIST,
      { params }
    );
    return response.data.data;
  },

  getById: async (id: number): Promise<EggProduction> => {
    const response = await axiosInstance.get<ApiResponse<EggProduction>>(
      API_ENDPOINTS.EGG_PRODUCTIONS.DETAIL(id)
    );
    return response.data.data;
  },

  create: async (data: Partial<EggProduction>): Promise<EggProduction> => {
    const response = await axiosInstance.post<ApiResponse<EggProduction>>(
      API_ENDPOINTS.EGG_PRODUCTIONS.CREATE,
      data
    );
    return response.data.data;
  },

  update: async (id: number, data: Partial<EggProduction>): Promise<EggProduction> => {
    const response = await axiosInstance.put<ApiResponse<EggProduction>>(
      API_ENDPOINTS.EGG_PRODUCTIONS.DETAIL(id),
      data
    );
    return response.data.data;
  },

  delete: async (id: number): Promise<void> => {
    await axiosInstance.delete(API_ENDPOINTS.EGG_PRODUCTIONS.DETAIL(id));
  },

  getStats: async (): Promise<any> => {
    const response = await axiosInstance.get<ApiResponse<any>>(API_ENDPOINTS.EGG_PRODUCTIONS.STATS);
    const stats = response.data.data;

    // Transform the API response to match what the UI expects
    const totalEggs =
      typeof stats.total_eggs_produced === 'string'
        ? parseInt(stats.total_eggs_produced.replace(/,/g, ''))
        : stats.total_eggs_produced;

    const totalBroken = stats.total_broken_eggs || 0;
    const totalGood = totalEggs - totalBroken;

    // Get today's production from the last entry in daily_trends
    let totalToday = 0;
    if (stats.daily_trends && stats.daily_trends.length > 0) {
      const latestTrend = stats.daily_trends[stats.daily_trends.length - 1];
      totalToday =
        typeof latestTrend.daily_total === 'string'
          ? parseInt(latestTrend.daily_total.replace(/,/g, ''))
          : latestTrend.daily_total;
    }

    return {
      ...stats,
      total_eggs: totalEggs,
      total_good_eggs: totalGood,
      good_eggs_percentage: totalEggs > 0 ? (totalGood / totalEggs) * 100 : 0,
      total_broken_eggs_display: totalBroken,
      broken_eggs_percentage: totalEggs > 0 ? (totalBroken / totalEggs) * 100 : 0,
      average_daily: stats.total_productions > 0 ? totalEggs / stats.total_productions : 0,
      total_today: totalToday,
    };
  },

  getTrends: async (params?: any): Promise<any> => {
    const response = await axiosInstance.get(API_ENDPOINTS.EGG_PRODUCTIONS.TRENDS, { params });
    return response.data.data;
  },

  getTopPerformance: async (): Promise<any> => {
    const response = await axiosInstance.get(API_ENDPOINTS.EGG_PRODUCTIONS.TOP_PERFORMANCE);
    return response.data.data;
  },

  getProductionCurve: async (params?: { procurement_id?: number }): Promise<any> => {
    const response = await axiosInstance.get(API_ENDPOINTS.EGG_PRODUCTIONS.PRODUCTION_CURVE, {
      params,
    });
    return response.data.data;
  },

  getBirdDayToDay: async (birdId: number, params?: any): Promise<any> => {
    const response = await axiosInstance.get(`/egg-productions/bird/${birdId}/day-to-day`, {
      params,
    });
    // API returns { bird, productions, pagination }
    // Return productions array with pagination for DataGrid
    return {
      data: response.data.data.productions,
      bird: response.data.data.bird,
      pagination: response.data.data.pagination,
    };
  },

  getBirdDayToDaySummary: async (birdId: number, params?: any): Promise<any> => {
    const response = await axiosInstance.get(`/egg-productions/bird/${birdId}/day-to-day-summary`, {
      params,
    });
    // API returns { bird, summary }
    // Return the summary directly for easier access
    return response.data.data.summary;
  },
};

// Egg Sales Service
export const eggSalesService = {
  getAll: async (params?: any): Promise<PaginatedResponse<EggSale>> => {
    const response = await axiosInstance.get<ApiResponse<any>>(API_ENDPOINTS.EGG_SALES.LIST, {
      params,
    });
    // Transform Laravel pagination to our format
    const laravelData = response.data.data;
    return {
      data: laravelData.data,
      meta: {
        current_page: laravelData.current_page,
        from: laravelData.from,
        last_page: laravelData.last_page,
        per_page: laravelData.per_page,
        to: laravelData.to,
        total: laravelData.total,
      },
    };
  },

  getById: async (id: number): Promise<EggSale> => {
    const response = await axiosInstance.get<ApiResponse<EggSale>>(
      API_ENDPOINTS.EGG_SALES.DETAIL(id)
    );
    return response.data.data;
  },

  create: async (data: Partial<EggSale>): Promise<EggSale> => {
    const response = await axiosInstance.post<ApiResponse<EggSale>>(
      API_ENDPOINTS.EGG_SALES.CREATE,
      data
    );
    return response.data.data;
  },

  createBatch: async (data: {
    customer_id: number;
    sales_date: string;
    items: Array<{
      procurement_id: number;
      crate_quantity: number;
      unit_price: number;
      amount_paid?: number;
      price_id?: number;
    }>;
  }): Promise<EggSale[]> => {
    const response = await axiosInstance.post<ApiResponse<EggSale[]>>(
      API_ENDPOINTS.EGG_SALES.BATCH,
      data
    );
    return response.data.data;
  },

  update: async (id: number, data: Partial<EggSale>): Promise<EggSale> => {
    const response = await axiosInstance.put<ApiResponse<EggSale>>(
      API_ENDPOINTS.EGG_SALES.DETAIL(id),
      data
    );
    return response.data.data;
  },

  delete: async (id: number): Promise<void> => {
    await axiosInstance.delete(API_ENDPOINTS.EGG_SALES.DETAIL(id));
  },

  bulkMarkPaid: async (params?: any): Promise<{ updated_count: number; message: string }> => {
    const response = await axiosInstance.post<ApiResponse<{ updated_count: number }>>(
      API_ENDPOINTS.EGG_SALES.BULK_MARK_PAID,
      params
    );
    return {
      updated_count: response.data.data.updated_count,
      message: response.data.message ?? '',
    };
  },

  getStats: async (params?: any): Promise<any> => {
    const response = await axiosInstance.get<ApiResponse<any>>(API_ENDPOINTS.EGG_SALES.STATS, {
      params,
    });
    const stats = response.data.data;

    // Convert amounts from cents to cedis
    return {
      total_revenue: parseFloat(stats.total_sales_value) / 100,
      total_paid: parseFloat(stats.total_amount_paid) / 100,
      total_outstanding: parseFloat(stats.total_amount_owed) / 100,
      total_quantity_sold: parseInt(stats.total_crates_sold),
      total_transactions: stats.total_transactions,
      paid_transactions: stats.paid_transactions,
      pending_sales_count: stats.total_transactions - stats.paid_transactions,
      payment_rate: stats.payment_rate,
      average_sale_value: stats.average_sale_value / 100,
      top_customers:
        stats.top_customers?.map((customer: any) => ({
          ...customer,
          total_spent: parseFloat(customer.total_spent) / 100,
          total_crates: parseInt(customer.total_crates),
        })) || [],
    };
  },

  getPending: async (): Promise<EggSale[]> => {
    const response = await axiosInstance.get<ApiResponse<EggSale[]>>(
      API_ENDPOINTS.EGG_SALES.PENDING
    );
    return response.data.data;
  },

  getTrends: async (params?: any): Promise<any> => {
    const response = await axiosInstance.get(API_ENDPOINTS.EGG_SALES.TRENDS, { params });
    return response.data.data;
  },

  markPaid: async (id: number, data: any): Promise<EggSale> => {
    const response = await axiosInstance.post<ApiResponse<EggSale>>(
      API_ENDPOINTS.EGG_SALES.MARK_PAID(id),
      data
    );
    return response.data.data;
  },
};

// Deposits Service
export const depositService = {
  getAll: async (params?: any): Promise<any> => {
    const response = await axiosInstance.get<ApiResponse<any>>(API_ENDPOINTS.DEPOSITS.LIST, {
      params,
    });
    return response.data.data;
  },

  create: async (data: any): Promise<any> => {
    const response = await axiosInstance.post<ApiResponse<any>>(
      API_ENDPOINTS.DEPOSITS.CREATE,
      data
    );
    return response.data.data;
  },

  bulkComplete: async (filters?: any): Promise<any> => {
    const response = await axiosInstance.post<ApiResponse<any>>(
      API_ENDPOINTS.DEPOSITS.BULK_COMPLETE,
      filters
    );
    return response.data;
  },

  reverseDeposits: async (filters?: any): Promise<any> => {
    const response = await axiosInstance.post<ApiResponse<any>>(
      API_ENDPOINTS.DEPOSITS.REVERSE,
      filters
    );
    return response.data;
  },

  correctDeposits: async (): Promise<any> => {
    const response = await axiosInstance.post<ApiResponse<any>>(API_ENDPOINTS.DEPOSITS.CORRECT);
    return response.data;
  },

  delete: async (id: number): Promise<void> => {
    await axiosInstance.delete(API_ENDPOINTS.DEPOSITS.DETAIL(id));
  },

  getStats: async (params?: any): Promise<any> => {
    const response = await axiosInstance.get<ApiResponse<any>>(API_ENDPOINTS.DEPOSITS.STATS, {
      params,
    });
    return response.data.data;
  },

  getSaleDeposits: async (eggSaleId: number): Promise<any> => {
    const response = await axiosInstance.get<ApiResponse<any>>(
      API_ENDPOINTS.DEPOSITS.SALE_DEPOSITS(eggSaleId)
    );
    return response.data.data;
  },
};

// Broken Egg Sales Service
export const brokenEggSalesService = {
  getAll: async (params?: any): Promise<any> => {
    const response = await axiosInstance.get<ApiResponse<any>>(
      API_ENDPOINTS.BROKEN_EGG_SALES.LIST,
      { params }
    );
    return response.data.data;
  },
  create: async (data: any): Promise<any> => {
    const response = await axiosInstance.post<ApiResponse<any>>(
      API_ENDPOINTS.BROKEN_EGG_SALES.CREATE,
      data
    );
    return response.data.data;
  },
  getStock: async (): Promise<any> => {
    const response = await axiosInstance.get<ApiResponse<any>>(
      API_ENDPOINTS.BROKEN_EGG_SALES.STOCK
    );
    return response.data.data;
  },
};

// Crate Stock Service
export const crateStockService = {
  getAll: async (params?: any): Promise<any> => {
    const response = await axiosInstance.get<ApiResponse<any>>(API_ENDPOINTS.CRATE_STOCKS.LIST, {
      params,
    });
    return response.data.data;
  },

  create: async (data: any): Promise<any> => {
    const response = await axiosInstance.post<ApiResponse<any>>(
      API_ENDPOINTS.CRATE_STOCKS.CREATE,
      data
    );
    return response.data.data;
  },

  createStockOnly: async (data: any): Promise<any> => {
    const response = await axiosInstance.post<ApiResponse<any>>(
      API_ENDPOINTS.CRATE_STOCKS.CREATE_STOCK_ONLY,
      data
    );
    return response.data.data;
  },

  update: async (id: number, data: any): Promise<any> => {
    const response = await axiosInstance.put<ApiResponse<any>>(
      API_ENDPOINTS.CRATE_STOCKS.DETAIL(id),
      data
    );
    return response.data.data;
  },

  delete: async (id: number): Promise<void> => {
    await axiosInstance.delete(API_ENDPOINTS.CRATE_STOCKS.DETAIL(id));
  },

  getStats: async (): Promise<any> => {
    const response = await axiosInstance.get<ApiResponse<any>>(API_ENDPOINTS.CRATE_STOCKS.STATS);
    return response.data.data;
  },

  getHistory: async (params?: any): Promise<any> => {
    const response = await axiosInstance.get<ApiResponse<any>>(API_ENDPOINTS.CRATE_STOCKS.HISTORY, {
      params,
    });
    return response.data.data;
  },
};

// Egg Stock Service
export const eggStockService = {
  getAll: async (): Promise<EggStock[]> => {
    const response = await axiosInstance.get<ApiResponse<EggStock[]>>(API_ENDPOINTS.EGG_STOCK.LIST);
    return response.data.data;
  },

  getSummary: async (): Promise<any> => {
    const response = await axiosInstance.get(API_ENDPOINTS.EGG_STOCK.SUMMARY);
    return response.data.data;
  },

  getAlerts: async (): Promise<any[]> => {
    const response = await axiosInstance.get<ApiResponse<any[]>>(API_ENDPOINTS.EGG_STOCK.ALERTS);
    return response.data.data;
  },
};

// Egg Prices Service
export const eggPricesService = {
  getAll: async (): Promise<EggPrice[]> => {
    const response = await axiosInstance.get<ApiResponse<EggPrice[]>>(
      API_ENDPOINTS.EGG_PRICES.LIST
    );
    return response.data.data;
  },

  create: async (data: Partial<EggPrice>): Promise<EggPrice> => {
    const response = await axiosInstance.post<ApiResponse<EggPrice>>(
      API_ENDPOINTS.EGG_PRICES.CREATE,
      data
    );
    return response.data.data;
  },

  getCurrent: async (): Promise<EggPrice[]> => {
    const response = await axiosInstance.get<ApiResponse<EggPrice[]>>(
      API_ENDPOINTS.EGG_PRICES.CURRENT
    );
    return response.data.data;
  },

  setCurrent: async (id: number): Promise<EggPrice> => {
    const response = await axiosInstance.post<ApiResponse<EggPrice>>(
      API_ENDPOINTS.EGG_PRICES.SET_CURRENT(id)
    );
    return response.data.data;
  },
};

// Customers Service
export const customersService = {
  getAll: async (params?: any): Promise<PaginatedResponse<Customer>> => {
    const response = await axiosInstance.get<ApiResponse<PaginatedResponse<Customer>>>(
      API_ENDPOINTS.CUSTOMERS.LIST,
      { params }
    );
    return response.data.data;
  },

  getById: async (id: number): Promise<Customer> => {
    const response = await axiosInstance.get<ApiResponse<Customer>>(
      API_ENDPOINTS.CUSTOMERS.DETAIL(id)
    );
    return response.data.data;
  },

  create: async (data: Partial<Customer>): Promise<Customer> => {
    const response = await axiosInstance.post<ApiResponse<Customer>>(
      API_ENDPOINTS.CUSTOMERS.CREATE,
      data
    );
    return response.data.data;
  },

  update: async (id: number, data: Partial<Customer>): Promise<Customer> => {
    const response = await axiosInstance.put<ApiResponse<Customer>>(
      API_ENDPOINTS.CUSTOMERS.DETAIL(id),
      data
    );
    return response.data.data;
  },

  delete: async (id: number): Promise<void> => {
    await axiosInstance.delete(API_ENDPOINTS.CUSTOMERS.DETAIL(id));
  },

  getStats: async (): Promise<CustomerStats> => {
    const response = await axiosInstance.get<ApiResponse<CustomerStats>>(
      API_ENDPOINTS.CUSTOMERS.STATS
    );
    return response.data.data;
  },

  getOutstanding: async (): Promise<OutstandingCustomersResponse> => {
    const response = await axiosInstance.get<ApiResponse<OutstandingCustomersResponse>>(
      API_ENDPOINTS.CUSTOMERS.OUTSTANDING
    );
    return response.data.data;
  },

  search: async (query: string): Promise<Customer[]> => {
    const response = await axiosInstance.get<ApiResponse<Customer[]>>(
      API_ENDPOINTS.CUSTOMERS.SEARCH,
      { params: { q: query } }
    );
    return response.data.data;
  },

  resendSms: async (id: number, date: string): Promise<{ message: string }> => {
    const response = await axiosInstance.post(API_ENDPOINTS.CUSTOMERS.RESEND_SMS(id), { date });
    return response.data;
  },

  getSmsLogs: async (params?: {
    customer_id?: number;
    status?: string;
    trigger?: string;
    date_from?: string;
    date_to?: string;
  }): Promise<any> => {
    const response = await axiosInstance.get(API_ENDPOINTS.CUSTOMERS.SMS_LOGS, { params });
    return response.data.data;
  },
};

// Financial Cycles Service
export const financialCyclesService = {
  getAll: async (): Promise<FinancialCycle[]> => {
    const response = await axiosInstance.get<ApiResponse<any>>(API_ENDPOINTS.FINANCIAL_CYCLES.LIST);
    const paginatedData = response.data.data;

    // Transform the paginated API response
    // The API returns amounts as strings with currency symbols, but we need numbers in cents
    const parseAmount = (amountStr: string): number => {
      // Remove currency symbol and commas, parse as float, convert to cents
      const cleaned = amountStr.replace(/[GH₵\s,]/g, '');
      return Math.round(parseFloat(cleaned) * 100);
    };

    return paginatedData.data.map((cycle: any) => ({
      id: cycle.id,
      name: cycle.cycle_number,
      start_date: cycle.start_date,
      end_date: cycle.end_date || undefined,
      status: cycle.status,
      total_revenue: parseAmount(cycle.total_sales),
      total_expenses: parseAmount(cycle.total_costs),
      net_profit: parseAmount(cycle.profit),
      created_at: cycle.created_at,
      updated_at: cycle.updated_at,
    }));
  },

  getCurrent: async (): Promise<FinancialCycle> => {
    const response = await axiosInstance.get<ApiResponse<any>>(
      API_ENDPOINTS.FINANCIAL_CYCLES.CURRENT
    );
    const data = response.data.data;

    // Transform the nested API response to flat FinancialCycle structure
    // The API returns amounts as strings with currency symbols, but we need numbers in cents
    const parseAmount = (amountStr: string): number => {
      // Remove currency symbol and commas, parse as float, convert to cents
      const cleaned = amountStr.replace(/[GH₵\s,]/g, '');
      return Math.round(parseFloat(cleaned) * 100);
    };

    return {
      id: data.cycle_info.id,
      name: data.cycle_info.cycle_number,
      start_date: data.cycle_info.start_date,
      end_date: data.cycle_info.end_date || undefined,
      status: data.cycle_info.status,
      total_revenue: parseAmount(data.financial_summary.total_sales),
      total_expenses: parseAmount(data.financial_summary.total_costs),
      net_profit: parseAmount(data.financial_summary.profit),
      created_at: data.cycle_info.start_date, // Using start_date as fallback
      updated_at: data.cycle_info.start_date, // Using start_date as fallback
    };
  },

  getById: async (id: string | number): Promise<any> => {
    const response = await axiosInstance.get<ApiResponse<any>>(
      API_ENDPOINTS.FINANCIAL_CYCLES.DETAIL(id)
    );
    return response.data.data;
  },

  getSummary: async (): Promise<any> => {
    const response = await axiosInstance.get(API_ENDPOINTS.FINANCIAL_CYCLES.SUMMARY);
    const data = response.data.data;

    // Helper to parse currency strings to cents
    const parseAmount = (amountStr: string): number => {
      const cleaned = amountStr.replace(/[GH₵\s,]/g, '');
      return Math.round(parseFloat(cleaned) * 100);
    };

    // Calculate totals from all cycles (current + recent closed)
    const allCycles = [
      ...(data.current_cycle ? [data.current_cycle] : []),
      ...(data.recent_closed_cycles || []),
    ];

    let totalRevenue = 0;
    let totalExpenses = 0;
    let totalProfit = 0;

    // Sum up from current cycle
    if (data.current_cycle?.financial_summary) {
      totalRevenue += parseAmount(data.current_cycle.financial_summary.total_sales);
      totalExpenses += parseAmount(data.current_cycle.financial_summary.total_costs);
      totalProfit += parseAmount(data.current_cycle.financial_summary.profit);
    }

    // Sum up from recent closed cycles
    if (data.recent_closed_cycles) {
      data.recent_closed_cycles.forEach((cycle: any) => {
        totalRevenue += parseAmount(cycle.total_sales);
        totalExpenses += parseAmount(cycle.total_costs);
        totalProfit += parseAmount(cycle.profit);
      });
    }

    // Calculate profit margin
    const profitMargin = totalRevenue > 0 ? (totalProfit / totalRevenue) * 100 : 0;

    return {
      total_cycles: allCycles.length,
      active_cycles: data.current_cycle ? 1 : 0,
      total_revenue: totalRevenue, // in cents
      total_expenses: totalExpenses, // in cents
      total_profit: totalProfit, // in cents
      profit_margin: profitMargin, // percentage
      current_cycle: data.current_cycle,
      upcoming_cycle: data.upcoming_cycle,
      recent_closed_cycles: data.recent_closed_cycles,
      cycle_status: data.cycle_status,
    };
  },

  getTrends: async (): Promise<any> => {
    const response = await axiosInstance.get(API_ENDPOINTS.FINANCIAL_CYCLES.TRENDS);
    return response.data.data;
  },

  close: async (data: any): Promise<FinancialCycle> => {
    const response = await axiosInstance.post<ApiResponse<FinancialCycle>>(
      API_ENDPOINTS.FINANCIAL_CYCLES.CLOSE,
      data
    );
    return response.data.data;
  },
};

// Expenditures Service
export const expendituresService = {
  getAll: async (params?: any): Promise<PaginatedResponse<ExpenditureGroup>> => {
    const response = await axiosInstance.get<ApiResponse<PaginatedResponse<ExpenditureGroup>>>(
      API_ENDPOINTS.EXPENDITURES.LIST,
      { params }
    );
    return response.data.data;
  },

  getById: async (id: number): Promise<Expenditure> => {
    const response = await axiosInstance.get<ApiResponse<Expenditure>>(
      API_ENDPOINTS.EXPENDITURES.DETAIL(id)
    );
    return response.data.data;
  },

  create: async (data: Partial<Expenditure>): Promise<Expenditure> => {
    const response = await axiosInstance.post<ApiResponse<Expenditure>>(
      API_ENDPOINTS.EXPENDITURES.CREATE,
      data
    );
    return response.data.data;
  },

  update: async (id: number, data: Partial<Expenditure>): Promise<Expenditure> => {
    const response = await axiosInstance.put<ApiResponse<Expenditure>>(
      API_ENDPOINTS.EXPENDITURES.DETAIL(id),
      data
    );
    return response.data.data;
  },

  delete: async (id: number): Promise<void> => {
    await axiosInstance.delete(API_ENDPOINTS.EXPENDITURES.DETAIL(id));
  },

  getStats: async (params?: any): Promise<Stats> => {
    const response = await axiosInstance.get<ApiResponse<Stats>>(API_ENDPOINTS.EXPENDITURES.STATS, {
      params,
    });
    return response.data.data;
  },

  getPending: async (): Promise<Expenditure[]> => {
    const response = await axiosInstance.get<ApiResponse<Expenditure[]>>(
      API_ENDPOINTS.EXPENDITURES.PENDING
    );
    return response.data.data;
  },

  getTrends: async (params?: { period?: string }): Promise<any> => {
    const response = await axiosInstance.get<ApiResponse<any>>(API_ENDPOINTS.EXPENDITURES.TRENDS, {
      params,
    });
    return response.data.data;
  },

  makePayment: async (id: number, data: any): Promise<Expenditure> => {
    const response = await axiosInstance.post<ApiResponse<Expenditure>>(
      API_ENDPOINTS.EXPENDITURES.MAKE_PAYMENT(id),
      data
    );
    return response.data.data;
  },

  getAllFlat: async (params?: any): Promise<PaginatedResponse<Expenditure>> => {
    const response = await axiosInstance.get<ApiResponse<any>>(API_ENDPOINTS.EXPENDITURES.ALL, {
      params,
    });
    const laravelData = response.data.data;
    return {
      data: laravelData.data,
      meta: {
        current_page: laravelData.current_page,
        from: laravelData.from,
        last_page: laravelData.last_page,
        per_page: laravelData.per_page,
        to: laravelData.to,
        total: laravelData.total,
      },
    };
  },
};

// Expenditure Categories Service
export const expenditureCategoriesService = {
  getAll: async (): Promise<ExpenditureCategory[]> => {
    const response = await axiosInstance.get<ApiResponse<ExpenditureCategory[]>>(
      API_ENDPOINTS.EXPENDITURE_CATEGORIES.LIST
    );
    return response.data.data;
  },

  getById: async (id: number): Promise<ExpenditureCategory> => {
    const response = await axiosInstance.get<ApiResponse<ExpenditureCategory>>(
      API_ENDPOINTS.EXPENDITURE_CATEGORIES.DETAIL(id)
    );
    return response.data.data;
  },

  create: async (data: Partial<ExpenditureCategory>): Promise<ExpenditureCategory> => {
    const response = await axiosInstance.post<ApiResponse<ExpenditureCategory>>(
      API_ENDPOINTS.EXPENDITURE_CATEGORIES.CREATE,
      data
    );
    return response.data.data;
  },

  update: async (id: number, data: Partial<ExpenditureCategory>): Promise<ExpenditureCategory> => {
    const response = await axiosInstance.put<ApiResponse<ExpenditureCategory>>(
      API_ENDPOINTS.EXPENDITURE_CATEGORIES.DETAIL(id),
      data
    );
    return response.data.data;
  },

  delete: async (id: number): Promise<void> => {
    await axiosInstance.delete(API_ENDPOINTS.EXPENDITURE_CATEGORIES.DETAIL(id));
  },

  getStats: async (): Promise<Stats> => {
    const response = await axiosInstance.get<ApiResponse<Stats>>(
      API_ENDPOINTS.EXPENDITURE_CATEGORIES.STATS
    );
    return response.data.data;
  },
};

// Mortalities Service
export const mortalitiesService = {
  getAll: async (params?: any): Promise<PaginatedResponse<any>> => {
    const response = await axiosInstance.get<ApiResponse<any>>(API_ENDPOINTS.MORTALITIES.LIST, {
      params,
    });
    // The API returns nested structure grouped by procurement
    // Return it as-is, let the component handle the structure
    const apiData = response.data.data;
    return {
      data: apiData.data, // Array of MortalityGroup
      meta: {
        current_page: apiData.current_page,
        from: apiData.from,
        last_page: apiData.last_page,
        per_page: apiData.per_page,
        to: apiData.to,
        total: apiData.total,
      },
    };
  },

  getById: async (id: number): Promise<Mortality> => {
    const response = await axiosInstance.get<ApiResponse<Mortality>>(
      API_ENDPOINTS.MORTALITIES.DETAIL(id)
    );
    return response.data.data;
  },

  create: async (data: Partial<Mortality>): Promise<Mortality> => {
    const response = await axiosInstance.post<ApiResponse<Mortality>>(
      API_ENDPOINTS.MORTALITIES.CREATE,
      data
    );
    return response.data.data;
  },

  update: async (id: number, data: Partial<Mortality>): Promise<Mortality> => {
    const response = await axiosInstance.put<ApiResponse<Mortality>>(
      API_ENDPOINTS.MORTALITIES.DETAIL(id),
      data
    );
    return response.data.data;
  },

  delete: async (id: number): Promise<void> => {
    await axiosInstance.delete(API_ENDPOINTS.MORTALITIES.DETAIL(id));
  },

  getStats: async (params?: any): Promise<MortalityStats> => {
    const response = await axiosInstance.get<ApiResponse<MortalityStats>>(
      API_ENDPOINTS.MORTALITIES.STATS,
      {
        params,
      }
    );
    return response.data.data;
  },

  getTrends: async (params?: any): Promise<any> => {
    const response = await axiosInstance.get(API_ENDPOINTS.MORTALITIES.TRENDS, { params });
    return response.data.data;
  },
};

// Decommission Service
export const decommissionService = {
  getAll: async (params?: any): Promise<PaginatedResponse<Decommission>> => {
    const response = await axiosInstance.get<ApiResponse<any>>(API_ENDPOINTS.DECOMMISSIONS.LIST, {
      params,
    });
    // Transform Laravel paginated response to our format
    const apiData = response.data.data;
    return {
      data: apiData.data,
      meta: {
        current_page: apiData.current_page,
        from: apiData.from,
        last_page: apiData.last_page,
        per_page: apiData.per_page,
        to: apiData.to,
        total: apiData.total,
      },
    };
  },

  getById: async (id: number): Promise<Decommission> => {
    const response = await axiosInstance.get<ApiResponse<Decommission>>(
      API_ENDPOINTS.DECOMMISSIONS.DETAIL(id)
    );
    return response.data.data;
  },

  create: async (data: Partial<Decommission>): Promise<Decommission> => {
    const response = await axiosInstance.post<ApiResponse<Decommission>>(
      API_ENDPOINTS.DECOMMISSIONS.CREATE,
      data
    );
    return response.data.data;
  },

  update: async (id: number, data: Partial<Decommission>): Promise<Decommission> => {
    const response = await axiosInstance.put<ApiResponse<Decommission>>(
      API_ENDPOINTS.DECOMMISSIONS.DETAIL(id),
      data
    );
    return response.data.data;
  },

  delete: async (id: number): Promise<void> => {
    await axiosInstance.delete(API_ENDPOINTS.DECOMMISSIONS.DETAIL(id));
  },

  getStats: async (): Promise<DecommissionStats> => {
    const response = await axiosInstance.get<ApiResponse<DecommissionStats>>(
      API_ENDPOINTS.DECOMMISSIONS.STATS
    );
    return response.data.data;
  },

  getTrends: async (params?: any): Promise<any> => {
    const response = await axiosInstance.get(API_ENDPOINTS.DECOMMISSIONS.TRENDS, { params });
    return response.data.data;
  },
};

// Feed Service
export const feedService = {
  getDashboard: async (): Promise<any> => {
    const response = await axiosInstance.get(API_ENDPOINTS.FEED.DASHBOARD);
    return response.data.data;
  },

  deactivateFeedAssignment: async (id: number): Promise<void> => {
    await axiosInstance.patch(`${API_ENDPOINTS.FEED.ASSIGNMENTS}/${id}/deactivate`);
  },

  getTypes: async (): Promise<FeedType[]> => {
    const response = await axiosInstance.get<ApiResponse<FeedType[]>>(API_ENDPOINTS.FEED.TYPES);
    return response.data.data;
  },

  getBrands: async (): Promise<FeedBrand[]> => {
    const response = await axiosInstance.get<ApiResponse<FeedBrand[]>>(API_ENDPOINTS.FEED.BRANDS);
    return response.data.data;
  },

  getStock: async (): Promise<FeedStock[]> => {
    const response = await axiosInstance.get<ApiResponse<FeedStock[]>>(API_ENDPOINTS.FEED.STOCK);
    return response.data.data;
  },

  getStockAlerts: async (): Promise<any[]> => {
    const response = await axiosInstance.get<ApiResponse<any[]>>(API_ENDPOINTS.FEED.STOCK_ALERTS);
    return response.data.data;
  },

  getProcurements: async (params?: any): Promise<PaginatedResponse<FeedProcurement>> => {
    const response = await axiosInstance.get<ApiResponse<PaginatedResponse<FeedProcurement>>>(
      API_ENDPOINTS.FEED.PROCUREMENTS,
      { params }
    );
    return response.data.data;
  },

  procureFeed: async (data: Partial<FeedProcurement>): Promise<FeedProcurement> => {
    const response = await axiosInstance.post<ApiResponse<FeedProcurement>>(
      API_ENDPOINTS.FEED.PROCUREMENTS,
      data
    );
    return response.data.data;
  },

  markDelivered: async (
    procurementId: number,
    data: { quantity_delivered_kg: number; delivery_date: string }
  ): Promise<FeedProcurement> => {
    const response = await axiosInstance.patch<ApiResponse<FeedProcurement>>(
      `/feed/procurements/${procurementId}/deliver`,
      data
    );
    return response.data.data;
  },

  cancelProcurement: async (procurementId: number): Promise<void> => {
    await axiosInstance.delete(`/feed/procurements/${procurementId}`);
  },

  updateStockThreshold: async (stockId: number, threshold: number): Promise<FeedStock> => {
    const response = await axiosInstance.put<ApiResponse<FeedStock>>(
      `/feed/stock/${stockId}/threshold`,
      { minimum_threshold_kg: threshold }
    );
    return response.data.data;
  },

  updateStockQuantity: async (
    stockId: number,
    quantityKg: number,
    reason?: string
  ): Promise<FeedStock> => {
    const response = await axiosInstance.put<ApiResponse<FeedStock>>(
      `/feed/stock/${stockId}/quantity`,
      { quantity_kg: quantityKg, reason }
    );
    return response.data.data;
  },

  getAssignments: async (params?: any): Promise<PaginatedResponse<FeedAssignment>> => {
    const response = await axiosInstance.get<ApiResponse<PaginatedResponse<FeedAssignment>>>(
      API_ENDPOINTS.FEED.ASSIGNMENTS,
      { params }
    );
    return response.data.data;
  },

  assignFeed: async (data: {
    birds_id: number;
    feed_type_id: number;
    feed_brand_id: number;
    consumption_kg: number;
    consumption_period: 'weekly' | 'daily';
    notes?: string;
    feed_procurement_id?: number;
    feed_production_id?: number;
  }): Promise<FeedAssignment> => {
    const response = await axiosInstance.post<ApiResponse<FeedAssignment>>(
      API_ENDPOINTS.FEED.ASSIGNMENTS,
      data
    );
    return response.data.data;
  },

  updateFeedAssignment: async (
    id: number,
    data: Partial<{
      daily_consumption_kg: number;
      feed_type_id: number;
      feed_brand_id: number;
      consumption_kg: number;
      consumption_period: 'weekly' | 'daily';
      allocation_multiplier: number;
      notes?: string;
      feed_procurement_id?: number;
      feed_production_id?: number;
    }>
  ): Promise<FeedAssignment> => {
    const response = await axiosInstance.patch<ApiResponse<FeedAssignment>>(
      `${API_ENDPOINTS.FEED.ASSIGNMENTS}/${id}`,
      data
    );
    return response.data.data;
  },

  deleteFeedAssignment: async (id: number): Promise<void> => {
    await axiosInstance.delete(`${API_ENDPOINTS.FEED.ASSIGNMENTS}/${id}`);
  },

  recordConsumption: async (data: {
    feed_assignment_id: number;
    consumption_date: string;
    quantity_kg: number;
    remarks?: string;
  }): Promise<any> => {
    const response = await axiosInstance.post<ApiResponse<any>>('/feed/consumption', data);
    return response.data.data;
  },

  getTrends: async (): Promise<any> => {
    const response = await axiosInstance.get(API_ENDPOINTS.FEED.TRENDS);
    return response.data.data;
  },

  getCostAnalysis: async (): Promise<any> => {
    const response = await axiosInstance.get(API_ENDPOINTS.FEED.COST_ANALYSIS);
    return response.data.data;
  },

  getFCR: async (params?: {
    date_from?: string;
    date_to?: string;
    period?: 'weekly' | 'monthly';
    procurement_id?: number;
  }): Promise<any> => {
    const response = await axiosInstance.get(API_ENDPOINTS.FEED.FCR, { params });
    return response.data.data;
  },

  calculate: async (data: { weight_kg: number; birds_count?: number; multiplier?: number }) => {
    const response = await axiosInstance.post(API_ENDPOINTS.FEED.CALCULATE, data);
    return response.data.data;
  },

  // Raw Materials
  getRawMaterials: async (): Promise<any[]> => {
    const response = await axiosInstance.get<ApiResponse<any[]>>('/feed/raw-materials');
    return response.data.data;
  },

  createRawMaterial: async (data: any): Promise<any> => {
    const response = await axiosInstance.post<ApiResponse<any>>('/feed/raw-materials', data);
    return response.data.data;
  },

  updateRawMaterial: async (id: number, data: any): Promise<any> => {
    const response = await axiosInstance.patch<ApiResponse<any>>(`/feed/raw-materials/${id}`, data);
    return response.data.data;
  },

  adjustRawMaterialStock: async (
    id: number,
    data: { new_quantity: number; reason?: string }
  ): Promise<any> => {
    const response = await axiosInstance.patch<ApiResponse<any>>(
      `/feed/raw-materials/${id}/adjust-stock`,
      data
    );
    return response.data.data;
  },

  getRawMaterialHistory: async (id: number): Promise<any> => {
    const response = await axiosInstance.get<ApiResponse<any>>(`/feed/raw-materials/${id}/history`);
    return response.data.data;
  },

  getRawMaterialAlerts: async (): Promise<any[]> => {
    const response = await axiosInstance.get<ApiResponse<any[]>>('/feed/raw-materials/alerts');
    return response.data.data;
  },

  procureRawMaterial: async (data: any): Promise<any> => {
    const response = await axiosInstance.post<ApiResponse<any>>(
      '/feed/raw-materials/procure',
      data
    );
    return response.data.data;
  },

  bulkProcureRawMaterials: async (data: { items: any[] }): Promise<any> => {
    const response = await axiosInstance.post<ApiResponse<any>>(
      '/feed/raw-materials/procure/bulk',
      data
    );
    return response.data.data;
  },

  getRawMaterialProcurements: async (): Promise<any[]> => {
    const response = await axiosInstance.get<ApiResponse<any[]>>(
      '/feed/raw-materials/procurements'
    );
    return response.data.data;
  },

  markRawMaterialDelivered: async (id: number, delivery_date?: string): Promise<any> => {
    const response = await axiosInstance.patch<ApiResponse<any>>(
      `/feed/raw-materials/procurements/${id}/deliver`,
      { delivery_date }
    );
    return response.data.data;
  },

  deleteRawMaterialProcurement: async (id: number): Promise<any> => {
    const response = await axiosInstance.delete<ApiResponse<void>>(
      `/feed/raw-materials/procurements/${id}`
    );
    return response.data;
  },

  getProcurementPayments: async (procurementId: number): Promise<any> => {
    const response = await axiosInstance.get<ApiResponse<any>>(
      `/feed/raw-materials/procurements/${procurementId}/payments`
    );
    return response.data.data;
  },

  addProcurementPayment: async (procurementId: number, data: any): Promise<any> => {
    const response = await axiosInstance.post<ApiResponse<any>>(
      `/feed/raw-materials/procurements/${procurementId}/payments`,
      data
    );
    return response.data.data;
  },

  deleteProcurementPayment: async (procurementId: number, paymentId: number): Promise<any> => {
    await axiosInstance.delete(
      `/feed/raw-materials/procurements/${procurementId}/payments/${paymentId}`
    );
  },

  updateRawMaterialProcurement: async (procurementId: number, data: any): Promise<any> => {
    const response = await axiosInstance.patch(
      `/feed/raw-materials/procurements/${procurementId}`,
      data
    );
    return response.data.data;
  },

  getSuppliers: async (params?: any): Promise<any[]> => {
    const response = await axiosInstance.get('/feed/suppliers', { params });
    return response.data.data;
  },

  createSupplier: async (data: { name: string; phone?: string; notes?: string }): Promise<any> => {
    const response = await axiosInstance.post('/feed/suppliers', data);
    return response.data.data;
  },

  updateSupplier: async (id: number, data: any): Promise<any> => {
    const response = await axiosInstance.patch(`/feed/suppliers/${id}`, data);
    return response.data.data;
  },

  toggleSupplierStatus: async (id: number): Promise<any> => {
    const response = await axiosInstance.post(`/feed/suppliers/${id}/toggle-status`);
    return response.data.data;
  },

  deleteSupplier: async (id: number): Promise<any> => {
    await axiosInstance.delete(`/feed/suppliers/${id}`);
  },

  getSupplierPayments: async (supplierId: number): Promise<any> => {
    const response = await axiosInstance.get(`/feed/suppliers/${supplierId}/payments`);
    return response.data.data;
  },

  addSupplierPayment: async (supplierId: number, data: any): Promise<any> => {
    const response = await axiosInstance.post(`/feed/suppliers/${supplierId}/payments`, data);
    return response.data.data;
  },

  deleteSupplierPayment: async (supplierId: number, paymentId: number): Promise<any> => {
    const response = await axiosInstance.delete(
      `/feed/suppliers/${supplierId}/payments/${paymentId}`
    );
    return response.data.data;
  },

  getSupplierRawMaterialUsage: async (supplierId: number): Promise<any> => {
    const response = await axiosInstance.get(`/feed/suppliers/${supplierId}/raw-material-usage`);
    return response.data.data;
  },

  getSupplyCategories: async (): Promise<any[]> => {
    const response = await axiosInstance.get('/feed/supply-categories');
    return response.data.data;
  },

  createSupplyCategory: async (data: { name: string }): Promise<any> => {
    const response = await axiosInstance.post('/feed/supply-categories', data);
    return response.data.data;
  },

  updateSupplyCategory: async (id: number, data: { name: string }): Promise<any> => {
    const response = await axiosInstance.patch(`/feed/supply-categories/${id}`, data);
    return response.data.data;
  },

  deleteSupplyCategory: async (id: number): Promise<any> => {
    const response = await axiosInstance.delete(`/feed/supply-categories/${id}`);
    return response.data;
  },

  toggleRawMaterialStatus: async (id: number): Promise<any> => {
    const response = await axiosInstance.post<ApiResponse<any>>(
      `/feed/raw-materials/${id}/toggle-status`
    );
    return response.data.data;
  },

  // Feed Production
  produceFeed: async (data: any): Promise<any> => {
    const response = await axiosInstance.post<ApiResponse<any>>('/feed/production', data);
    return response.data.data;
  },

  getFeedProductions: async ({
    feed_brand_id,
    feed_type_id,
    date_from,
    date_to,
    bird_procurement_id,
  }: {
    feed_brand_id?: number;
    feed_type_id?: number;
    date_from?: string;
    date_to?: string;
    bird_procurement_id?: number;
  }): Promise<any[]> => {
    const response = await axiosInstance.get<ApiResponse<any[]>>('/feed/productions', {
      params: {
        brand: feed_brand_id,
        type: feed_type_id,
        date_from,
        date_to,
        procurement: bird_procurement_id,
      },
    });
    return response.data.data;
  },

  getProfitBaseline: async (days = 90): Promise<any> => {
    const response = await axiosInstance.get<ApiResponse<any>>(
      '/feed/productions/profit-baseline',
      { params: { days } }
    );
    return response.data.data;
  },

  getFeedProductionDetail: async (id: number): Promise<any> => {
    const response = await axiosInstance.get<ApiResponse<any>>(`/feed/productions/${id}`);
    return response.data.data;
  },

  updateFeedProduction: async ({ id, data }: { id: number; data: any }): Promise<any> => {
    const response = await axiosInstance.put<ApiResponse<any>>(`/feed/productions/${id}`, data);
    return response.data.data;
  },

  deleteFeedProduction: async (id: number): Promise<void> => {
    await axiosInstance.delete(`/feed/productions/${id}`);
  },

  autoAssignProduction: async (id: number): Promise<any> => {
    const response = await axiosInstance.post(`/feed/productions/${id}/auto-assign`);
    return response.data;
  },

  // Feed Production Templates
  getTemplates: async (params?: {
    active?: boolean;
    feed_type_id?: number;
    feed_brand_id?: number;
  }): Promise<any[]> => {
    const response = await axiosInstance.get<ApiResponse<any[]>>('/feed/templates', { params });
    return response.data.data;
  },

  createTemplate: async (data: any): Promise<any> => {
    const response = await axiosInstance.post<ApiResponse<any>>('/feed/templates', data);
    return response.data.data;
  },

  updateTemplate: async (id: number, data: any): Promise<any> => {
    const response = await axiosInstance.put<ApiResponse<any>>(`/feed/templates/${id}`, data);
    return response.data.data;
  },

  deleteTemplate: async (id: number): Promise<void> => {
    await axiosInstance.delete(`/feed/templates/${id}`);
  },

  applyTemplate: async (id: number, quantityKg?: number): Promise<any> => {
    const response = await axiosInstance.post<ApiResponse<any>>(`/feed/templates/${id}/apply`, {
      quantity_kg: quantityKg,
    });
    return response.data.data;
  },

  // Procurement Planner
  getProcurementPlanner: async (weeks = 4): Promise<any> => {
    const response = await axiosInstance.get<ApiResponse<any>>('/feed/procurement-planner', {
      params: { weeks },
    });
    return response.data.data;
  },

  // Feed Transition Schedule
  getTransitionSchedule: async (brand = 'layermax'): Promise<any> => {
    const response = await axiosInstance.get<ApiResponse<any>>('/feed/transition-schedule', {
      params: { brand },
    });
    return response.data.data;
  },

  // Feed Switch Campaigns
  getSwitchCampaigns: async (): Promise<any[]> => {
    const response = await axiosInstance.get<ApiResponse<any>>('/feed/switch-campaigns');
    return response.data.data;
  },
  getSwitchCampaign: async (id: number): Promise<any> => {
    const response = await axiosInstance.get<ApiResponse<any>>(`/feed/switch-campaigns/${id}`);
    return response.data.data;
  },
  createSwitchCampaign: async (data: any): Promise<any> => {
    const response = await axiosInstance.post<ApiResponse<any>>('/feed/switch-campaigns', data);
    return response.data.data;
  },
  updateSwitchCampaign: async (id: number, data: any): Promise<any> => {
    const response = await axiosInstance.patch<ApiResponse<any>>(
      `/feed/switch-campaigns/${id}`,
      data
    );
    return response.data.data;
  },
  addSwitchGroup: async (campaignId: number, data: any): Promise<any> => {
    const response = await axiosInstance.post<ApiResponse<any>>(
      `/feed/switch-campaigns/${campaignId}/groups`,
      data
    );
    return response.data.data;
  },
  updateSwitchGroup: async (groupId: number, data: any): Promise<any> => {
    const response = await axiosInstance.patch<ApiResponse<any>>(
      `/feed/switch-groups/${groupId}`,
      data
    );
    return response.data.data;
  },
  logSwitchDaily: async (groupId: number, data: any): Promise<any> => {
    const response = await axiosInstance.post<ApiResponse<any>>(
      `/feed/switch-groups/${groupId}/logs`,
      data
    );
    return response.data.data;
  },
  getSwitchGroupLogs: async (groupId: number): Promise<any[]> => {
    const response = await axiosInstance.get<ApiResponse<any>>(
      `/feed/switch-groups/${groupId}/logs`
    );
    return response.data.data;
  },
  deleteSwitchCampaign: async (id: number): Promise<void> => {
    await axiosInstance.delete(`/feed/switch-campaigns/${id}`);
  },
};

// Bird Weights Service
export const birdWeightsService = {
  getAll: async (params?: any): Promise<PaginatedResponse<any>> => {
    const response = await axiosInstance.get<ApiResponse<PaginatedResponse<any>>>(
      API_ENDPOINTS.BIRD_WEIGHTS.LIST,
      { params }
    );
    return response.data.data;
  },

  create: async (data: {
    bird_id: number;
    average_weight_kg: number;
    sample_size: number;
    weigh_date: string;
    notes?: string;
  }): Promise<any> => {
    const response = await axiosInstance.post<ApiResponse<any>>(
      API_ENDPOINTS.BIRD_WEIGHTS.CREATE,
      data
    );
    return response.data.data;
  },

  getById: async (id: number): Promise<any> => {
    const response = await axiosInstance.get<ApiResponse<any>>(
      API_ENDPOINTS.BIRD_WEIGHTS.DETAIL(id)
    );
    return response.data.data;
  },

  update: async (id: number, data: Partial<any>): Promise<any> => {
    const response = await axiosInstance.put<ApiResponse<any>>(
      API_ENDPOINTS.BIRD_WEIGHTS.UPDATE(id),
      data
    );
    return response.data.data;
  },

  delete: async (id: number): Promise<void> => {
    await axiosInstance.delete(API_ENDPOINTS.BIRD_WEIGHTS.DELETE(id));
  },

  getBirdHistory: async (birdId: number): Promise<any[]> => {
    const response = await axiosInstance.get<ApiResponse<any[]>>(
      API_ENDPOINTS.BIRD_WEIGHTS.BIRD_HISTORY(birdId)
    );
    return response.data.data;
  },

  getBirdLatest: async (birdId: number): Promise<any> => {
    const response = await axiosInstance.get<ApiResponse<any>>(
      API_ENDPOINTS.BIRD_WEIGHTS.BIRD_LATEST(birdId)
    );
    return response.data.data;
  },

  getBirdStats: async (birdId: number): Promise<any> => {
    const response = await axiosInstance.get<ApiResponse<any>>(
      API_ENDPOINTS.BIRD_WEIGHTS.BIRD_STATS(birdId)
    );
    return response.data.data;
  },
};

// Requests Service
export const requestsService = {
  getAll: async (params?: any): Promise<PaginatedResponse<Request>> => {
    const response = await axiosInstance.get<{
      meta: PaginationMeta;
      data: Request[];
    }>(API_ENDPOINTS.REQUESTS.LIST, { params });

    return {
      data: response.data.data,
      meta: response.data.meta,
    };
  },

  getById: async (id: number): Promise<Request> => {
    const response = await axiosInstance.get<ApiResponse<Request>>(
      API_ENDPOINTS.REQUESTS.DETAIL(id)
    );
    return response.data.data;
  },

  create: async (data: Partial<Request>): Promise<Request> => {
    const response = await axiosInstance.post<ApiResponse<Request>>(
      API_ENDPOINTS.REQUESTS.CREATE,
      data
    );
    return response.data.data;
  },

  update: async (id: number, data: Partial<Request>): Promise<Request> => {
    const response = await axiosInstance.put<ApiResponse<Request>>(
      API_ENDPOINTS.REQUESTS.DETAIL(id),
      data
    );
    return response.data.data;
  },

  getStats: async (): Promise<Stats> => {
    const response = await axiosInstance.get<ApiResponse<Stats>>(API_ENDPOINTS.REQUESTS.STATS);
    return response.data.data;
  },

  assign: async (id: number, userId: number): Promise<Request> => {
    const response = await axiosInstance.post<ApiResponse<Request>>(
      API_ENDPOINTS.REQUESTS.ASSIGN(id),
      { user_id: userId }
    );
    return response.data.data;
  },

  markInProgress: async (id: number): Promise<Request> => {
    const response = await axiosInstance.post<ApiResponse<Request>>(
      API_ENDPOINTS.REQUESTS.IN_PROGRESS(id)
    );
    return response.data.data;
  },

  complete: async (id: number, data: any): Promise<Request> => {
    const response = await axiosInstance.post<ApiResponse<Request>>(
      API_ENDPOINTS.REQUESTS.COMPLETE(id),
      data
    );
    return response.data.data;
  },
};

// Bird Blocks Service
export const birdBlocksService = {
  getAll: async (birdId: number): Promise<any[]> => {
    const response = await axiosInstance.get(API_ENDPOINTS.BIRDS.BLOCKS(birdId));
    return response.data.data;
  },

  create: async (birdId: number, data: { blocks: any[] }): Promise<any[]> => {
    const response = await axiosInstance.post(API_ENDPOINTS.BIRDS.BLOCKS(birdId), data);
    return response.data.data;
  },

  update: async (birdId: number, blockId: number, data: any): Promise<any> => {
    const response = await axiosInstance.put(API_ENDPOINTS.BIRDS.BLOCK(birdId, blockId), data);
    return response.data.data;
  },

  delete: async (birdId: number, blockId: number): Promise<void> => {
    await axiosInstance.delete(API_ENDPOINTS.BIRDS.BLOCK(birdId, blockId));
  },

  feedAll: async (birdId: number): Promise<any> => {
    const response = await axiosInstance.get(API_ENDPOINTS.BIRDS.BLOCKS_FEED(birdId));
    return response.data.data;
  },
};

// Users Service
export const usersService = {
  getAll: async (): Promise<any[]> => {
    const response = await axiosInstance.get<ApiResponse<any[]>>(API_ENDPOINTS.MISC.USERS);
    return response.data.data;
  },
};

// Security Service
export const securityService = {
  getUsers: async (): Promise<UserPermissions[]> => {
    const response = await axiosInstance.get<ApiResponse<UserPermissions[]>>(
      API_ENDPOINTS.SECURITY.USERS
    );
    return response.data.data;
  },

  getSupervisors: async (): Promise<User[]> => {
    const response = await axiosInstance.get<ApiResponse<User[]>>(
      API_ENDPOINTS.SECURITY.SUPERVISORS
    );
    return response.data.data;
  },

  getPermissions: async (): Promise<Permission[]> => {
    const response = await axiosInstance.get<ApiResponse<Permission[]>>(
      API_ENDPOINTS.SECURITY.PERMISSIONS
    );
    return response.data.data;
  },

  getUserPermissions: async (userId: number): Promise<UserPermissions> => {
    const response = await axiosInstance.get<ApiResponse<UserPermissions>>(
      API_ENDPOINTS.SECURITY.USER_PERMISSIONS(userId)
    );
    return response.data.data;
  },

  assignPermissions: async (userId: number, permissionIds: number[]): Promise<UserPermissions> => {
    const response = await axiosInstance.post<ApiResponse<UserPermissions>>(
      API_ENDPOINTS.SECURITY.ASSIGN_PERMISSIONS(userId),
      { permission_ids: permissionIds }
    );
    return response.data.data;
  },

  grantPermission: async (userId: number, permissionId: number): Promise<UserPermissions> => {
    const response = await axiosInstance.post<ApiResponse<UserPermissions>>(
      API_ENDPOINTS.SECURITY.GRANT_PERMISSION(userId),
      { permission_id: permissionId }
    );
    return response.data.data;
  },

  revokePermission: async (userId: number, permissionId: number): Promise<void> => {
    await axiosInstance.delete(API_ENDPOINTS.SECURITY.REVOKE_PERMISSION(userId, permissionId));
  },

  assignResourcePermissions: async (
    userId: number,
    resource: string,
    permissionIds: number[]
  ): Promise<UserPermissions> => {
    const response = await axiosInstance.post<ApiResponse<UserPermissions>>(
      API_ENDPOINTS.SECURITY.RESOURCE_PERMISSIONS(userId),
      { resource, permission_ids: permissionIds }
    );
    return response.data.data;
  },

  revokeResourcePermissions: async (userId: number, resource: string): Promise<void> => {
    await axiosInstance.delete(API_ENDPOINTS.SECURITY.RESOURCE_PERMISSIONS(userId), {
      data: { resource },
    });
  },

  getStatistics: async (): Promise<any> => {
    const response = await axiosInstance.get<ApiResponse<any>>(API_ENDPOINTS.SECURITY.STATISTICS);
    return response.data.data;
  },

  toggleActive: async (userId: number): Promise<{ id: number; is_active: boolean }> => {
    const response = await axiosInstance.patch(API_ENDPOINTS.SECURITY.TOGGLE_ACTIVE(userId));
    return response.data.data;
  },
};

// Alerts Service
export const alertsService = {
  getAll: async (): Promise<any[]> => {
    const response = await axiosInstance.get<ApiResponse<any[]>>(API_ENDPOINTS.ALERTS.LIST);
    return response.data.data;
  },

  getCount: async (): Promise<number> => {
    const response = await axiosInstance.get<ApiResponse<{ count: number }>>(
      API_ENDPOINTS.ALERTS.COUNT
    );
    return response.data.data.count;
  },
};

// Vet Instructions Service
export const vetInstructionsService = {
  getAll: async (params?: any): Promise<{ data: any[]; pagination: any }> => {
    const response = await axiosInstance.get<any>(API_ENDPOINTS.VET_INSTRUCTIONS.LIST, { params });
    return {
      data: response.data.data,
      pagination: response.data.pagination,
    };
  },

  getById: async (id: number): Promise<any> => {
    const response = await axiosInstance.get<ApiResponse<any>>(
      API_ENDPOINTS.VET_INSTRUCTIONS.SHOW(id)
    );
    return response.data.data;
  },

  create: async (data: any): Promise<any> => {
    const response = await axiosInstance.post<ApiResponse<any>>(
      API_ENDPOINTS.VET_INSTRUCTIONS.CREATE,
      data
    );
    return response.data.data;
  },

  complete: async (id: number, data: any): Promise<any> => {
    const response = await axiosInstance.post<ApiResponse<any>>(
      API_ENDPOINTS.VET_INSTRUCTIONS.COMPLETE(id),
      data
    );
    return response.data.data;
  },
};

// Capital Investment Service
export const capitalInvestmentService = {
  getAll: async (): Promise<any[]> => {
    const response = await axiosInstance.get<any>(API_ENDPOINTS.CAPITAL_INVESTMENTS.LIST);
    return response.data;
  },
  create: async (data: any): Promise<any> => {
    const response = await axiosInstance.post<any>(API_ENDPOINTS.CAPITAL_INVESTMENTS.LIST, data);
    return response.data;
  },
  update: async (id: number, data: any): Promise<any> => {
    const response = await axiosInstance.put<any>(
      API_ENDPOINTS.CAPITAL_INVESTMENTS.DETAIL(id),
      data
    );
    return response.data;
  },
  remove: async (id: number): Promise<void> => {
    await axiosInstance.delete(API_ENDPOINTS.CAPITAL_INVESTMENTS.DETAIL(id));
  },
  getContracts: async (id: number): Promise<any[]> => {
    const response = await axiosInstance.get<any>(`/capital-investments/${id}/contracts`);
    return response.data;
  },
  addContract: async (id: number, data: any): Promise<any> => {
    const response = await axiosInstance.post(`/capital-investments/${id}/contracts`, data);
    return response.data;
  },
  updateContract: async (investmentId: number, contractId: number, data: any): Promise<any> => {
    const response = await axiosInstance.patch(
      `/capital-investments/${investmentId}/contracts/${contractId}`,
      data
    );
    return response.data;
  },
  deleteContract: async (investmentId: number, contractId: number): Promise<void> => {
    await axiosInstance.delete(`/capital-investments/${investmentId}/contracts/${contractId}`);
  },
  getPayments: async (id: number): Promise<any[]> => {
    const response = await axiosInstance.get<any>(`/capital-investments/${id}/payments`);
    return response.data;
  },
  addPayment: async (id: number, data: any): Promise<any> => {
    const response = await axiosInstance.post<any>(`/capital-investments/${id}/payments`, data);
    return response.data;
  },
  updatePayment: async (investmentId: number, paymentId: number, data: any): Promise<any> => {
    const response = await axiosInstance.patch(
      `/capital-investments/${investmentId}/payments/${paymentId}`,
      data
    );
    return response.data;
  },
  deletePayment: async (investmentId: number, paymentId: number): Promise<void> => {
    await axiosInstance.delete(`/capital-investments/${investmentId}/payments/${paymentId}`);
  },
  complete: async (id: number, data: any): Promise<any> => {
    const response = await axiosInstance.post<any>(`/capital-investments/${id}/complete`, data);
    return response.data;
  },
};

// Vaccination Service
export const vaccinationService = {
  getAlerts: async (): Promise<any[]> => {
    const response = await axiosInstance.get<any>(API_ENDPOINTS.VACCINATIONS.ALERTS);
    return response.data;
  },

  getSchedule: async (): Promise<any[]> => {
    const response = await axiosInstance.get<any>(API_ENDPOINTS.VACCINATIONS.SCHEDULE);
    return response.data;
  },

  markCompleted: async (
    procurementId: number,
    vaccinationKey: string,
    deliveryDate: string
  ): Promise<any> => {
    const response = await axiosInstance.post<any>(
      API_ENDPOINTS.VACCINATIONS.MARK_COMPLETED(procurementId),
      { vaccination_key: vaccinationKey, delivery_date: deliveryDate }
    );
    return response.data;
  },
};

// Pricing Service
export const pricingService = {
  getActivePrices: async (): Promise<any> => {
    const response = await axiosInstance.get<ApiResponse<any>>('/pricing/prices/active');
    return response.data.data;
  },

  getCustomerPrice: async (customerId: number, crateQuantity: number): Promise<any> => {
    const response = await axiosInstance.get<ApiResponse<any>>('/pricing/customer-price', {
      params: { customer_id: customerId, crate_quantity: crateQuantity },
    });
    return response.data.data;
  },

  getAll: async (params?: any): Promise<PaginatedResponse<any>> => {
    const response = await axiosInstance.get<ApiResponse<PaginatedResponse<any>>>(
      '/pricing/prices',
      {
        params,
      }
    );
    return response.data.data;
  },

  create: async (data: any): Promise<any> => {
    const response = await axiosInstance.post<ApiResponse<any>>('/pricing/prices', data);
    return response.data.data;
  },

  update: async (id: number, data: any): Promise<any> => {
    const response = await axiosInstance.put<ApiResponse<any>>(`/pricing/prices/${id}`, data);
    return response.data.data;
  },

  delete: async (id: number): Promise<void> => {
    await axiosInstance.delete(`/pricing/prices/${id}`);
  },

  toggleStatus: async (id: number): Promise<any> => {
    const response = await axiosInstance.post<ApiResponse<any>>(
      `/pricing/prices/${id}/toggle-status`
    );
    return response.data.data;
  },

  getPricingSuggestions: async (params?: {
    period?: number;
    target_margin?: number;
  }): Promise<any> => {
    const response = await axiosInstance.get<ApiResponse<any>>('/pricing/suggestions', {
      params,
    });
    return response.data.data;
  },

  getQuickRecommendation: async (): Promise<any> => {
    const response = await axiosInstance.get<ApiResponse<any>>('/pricing/quick-recommendation');
    return response.data.data;
  },
};

// Employee Service
export const employeeService = {
  getAll: async (params?: any): Promise<PaginatedResponse<any>> => {
    const response = await axiosInstance.get<ApiResponse<any>>('/employees', {
      params,
    });
    // Transform Laravel pagination format
    const laravelData = response.data.data;
    return {
      data: laravelData.data,
      meta: {
        current_page: laravelData.current_page,
        from: laravelData.from,
        last_page: laravelData.last_page,
        per_page: laravelData.per_page,
        to: laravelData.to,
        total: laravelData.total,
      },
    };
  },

  getById: async (id: number): Promise<any> => {
    const response = await axiosInstance.get<ApiResponse<any>>(`/employees/${id}`);
    return response.data.data;
  },

  create: async (data: any): Promise<any> => {
    const response = await axiosInstance.post<ApiResponse<any>>('/employees', data);
    return response.data.data;
  },

  update: async (id: number, data: any): Promise<any> => {
    const response = await axiosInstance.put<ApiResponse<any>>(`/employees/${id}`, data);
    return response.data.data;
  },

  updateSalary: async (id: number, data: any): Promise<any> => {
    const response = await axiosInstance.post<ApiResponse<any>>(`/employees/${id}/salary`, data);
    return response.data.data;
  },

  toggleStatus: async (id: number): Promise<any> => {
    const response = await axiosInstance.post<ApiResponse<any>>(`/employees/${id}/toggle-status`);
    return response.data.data;
  },

  delete: async (id: number): Promise<void> => {
    await axiosInstance.delete(`/employees/${id}`);
  },

  getSalaryHistory: async (id: number): Promise<any[]> => {
    const response = await axiosInstance.get<ApiResponse<any[]>>(`/employees/${id}/salary-history`);
    return response.data.data;
  },
};

// Medication Service
export const medicationService = {
  getAll: async (filters?: any): Promise<PaginatedResponse<any>> => {
    const response = await axiosInstance.get<ApiResponse<PaginatedResponse<any>>>(
      API_ENDPOINTS.MEDICATIONS.LIST,
      { params: filters }
    );
    return response.data.data;
  },

  getStatistics: async (): Promise<any> => {
    const response = await axiosInstance.get<ApiResponse<any>>(
      API_ENDPOINTS.MEDICATIONS.STATISTICS
    );
    return response.data.data;
  },

  getTypes: async (): Promise<string[]> => {
    const response = await axiosInstance.get<ApiResponse<string[]>>(
      API_ENDPOINTS.MEDICATIONS.TYPES
    );
    return response.data.data;
  },

  getById: async (id: number): Promise<any> => {
    const response = await axiosInstance.get<ApiResponse<any>>(
      API_ENDPOINTS.MEDICATIONS.DETAIL(id)
    );
    return response.data.data;
  },

  create: async (data: any): Promise<any> => {
    const response = await axiosInstance.post<ApiResponse<any>>(
      API_ENDPOINTS.MEDICATIONS.CREATE,
      data
    );
    return response.data.data;
  },

  update: async (id: number, data: any): Promise<any> => {
    const response = await axiosInstance.put<ApiResponse<any>>(
      API_ENDPOINTS.MEDICATIONS.UPDATE(id),
      data
    );
    return response.data.data;
  },

  delete: async (id: number): Promise<void> => {
    await axiosInstance.delete(API_ENDPOINTS.MEDICATIONS.DELETE(id));
  },

  recordPurchase: async (id: number, data: any): Promise<any> => {
    const response = await axiosInstance.post<ApiResponse<any>>(
      API_ENDPOINTS.MEDICATIONS.PURCHASE(id),
      data
    );
    return response.data.data;
  },

  recordUsage: async (id: number, data: any): Promise<any> => {
    const response = await axiosInstance.post<ApiResponse<any>>(
      API_ENDPOINTS.MEDICATIONS.USAGE(id),
      data
    );
    return response.data.data;
  },

  recordAdjustment: async (id: number, data: any): Promise<any> => {
    const response = await axiosInstance.post<ApiResponse<any>>(
      API_ENDPOINTS.MEDICATIONS.ADJUSTMENT(id),
      data
    );
    return response.data.data;
  },

  recordDisposal: async (id: number, data: any): Promise<any> => {
    const response = await axiosInstance.post<ApiResponse<any>>(
      API_ENDPOINTS.MEDICATIONS.DISPOSAL(id),
      data
    );
    return response.data.data;
  },

  getTransactions: async (id: number, filters?: any): Promise<PaginatedResponse<any>> => {
    const response = await axiosInstance.get<ApiResponse<PaginatedResponse<any>>>(
      API_ENDPOINTS.MEDICATIONS.TRANSACTIONS(id),
      { params: filters }
    );
    return response.data.data;
  },

  getAllTransactions: async (filters?: any): Promise<PaginatedResponse<any>> => {
    const response = await axiosInstance.get<ApiResponse<PaginatedResponse<any>>>(
      API_ENDPOINTS.MEDICATIONS.ALL_TRANSACTIONS,
      { params: filters }
    );
    return response.data.data;
  },

  deleteTransaction: async (id: number): Promise<void> => {
    await axiosInstance.delete(API_ENDPOINTS.MEDICATIONS.DELETE_TRANSACTION(id));
  },
};

export const vetVisitReportService = {
  getAll: async (params?: Record<string, any>): Promise<any> => {
    const response = await axiosInstance.get(API_ENDPOINTS.VET_VISIT_REPORTS.LIST, { params });
    return response.data;
  },
  create: async (data: any): Promise<any> => {
    const response = await axiosInstance.post(API_ENDPOINTS.VET_VISIT_REPORTS.LIST, data);
    return response.data.data;
  },
  update: async (id: number, data: any): Promise<any> => {
    const response = await axiosInstance.patch(API_ENDPOINTS.VET_VISIT_REPORTS.DETAIL(id), data);
    return response.data.data;
  },
  remove: async (id: number): Promise<void> => {
    await axiosInstance.delete(API_ENDPOINTS.VET_VISIT_REPORTS.DETAIL(id));
  },
};

export const supplyProcurementService = {
  getAll: async (params?: Record<string, any>): Promise<any> => {
    const response = await axiosInstance.get(API_ENDPOINTS.SUPPLY_PROCUREMENTS.LIST, { params });
    return response.data;
  },
  create: async (data: any): Promise<any> => {
    const response = await axiosInstance.post(API_ENDPOINTS.SUPPLY_PROCUREMENTS.LIST, data);
    return response.data.data;
  },
  update: async (id: number, data: any): Promise<any> => {
    const response = await axiosInstance.patch(API_ENDPOINTS.SUPPLY_PROCUREMENTS.DETAIL(id), data);
    return response.data.data;
  },
  remove: async (id: number): Promise<void> => {
    await axiosInstance.delete(API_ENDPOINTS.SUPPLY_PROCUREMENTS.DETAIL(id));
  },
  makePayment: async (id: number, amount: number): Promise<any> => {
    const response = await axiosInstance.post(API_ENDPOINTS.SUPPLY_PROCUREMENTS.PAYMENT(id), {
      amount,
    });
    return response.data.data;
  },
};

export const cashFlowService = {
  getProjection: async (params?: { procurement_id?: number }): Promise<any> => {
    const response = await axiosInstance.get(API_ENDPOINTS.CASH_FLOW.PROJECTION, { params });
    return response.data.data;
  },
};

export const financialSummaryService = {
  getMonthly: async (params?: { year?: number }): Promise<any> => {
    const response = await axiosInstance.get(API_ENDPOINTS.FINANCIAL_SUMMARY.MONTHLY, { params });
    return response.data.data;
  },
};

export const weightSamplesService = {
  recordSamples: async (
    birdId: number,
    data: { block_id: number; week: number; group: 'main' | 'isolated'; weights: number[] }
  ): Promise<any> => {
    const r = await axiosInstance.post(API_ENDPOINTS.BIRDS.WEIGHT_SAMPLES(birdId), data);
    return r.data.data;
  },
  getTrend: async (birdId: number): Promise<any> => {
    const r = await axiosInstance.get(API_ENDPOINTS.BIRDS.WEIGHT_SAMPLES_TREND(birdId));
    return r.data.data;
  },
  getWeekStats: async (birdId: number, week: number): Promise<any> => {
    const r = await axiosInstance.get(API_ENDPOINTS.BIRDS.WEIGHT_SAMPLES_WEEK(birdId, week));
    return r.data.data;
  },
  deleteWeek: async (birdId: number, week: number): Promise<void> => {
    await axiosInstance.delete(API_ENDPOINTS.BIRDS.WEIGHT_SAMPLES_WEEK(birdId, week));
  },
  updateSample: async (birdId: number, sampleId: number, weightG: number): Promise<any> => {
    const r = await axiosInstance.patch(
      `${API_ENDPOINTS.BIRDS.WEIGHT_SAMPLES(birdId)}/${sampleId}`,
      { weight_g: weightG }
    );
    return r.data.data;
  },
  regroupBlock: async (
    birdId: number,
    data: { block_id: number; week: number; group: 'main' | 'isolated' }
  ): Promise<any> => {
    const r = await axiosInstance.patch(API_ENDPOINTS.BIRDS.WEIGHT_SAMPLES_REGROUP(birdId), data);
    return r.data.data;
  },
  extractImage: async (
    birdId: number,
    formData: FormData
  ): Promise<{ weight_g: number | null; note: string }> => {
    const r = await axiosInstance.post(
      API_ENDPOINTS.BIRDS.WEIGHT_SAMPLES_EXTRACT(birdId),
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return r.data.data;
  },
};

export const weightSubmissionsService = {
  list: async (params?: { status?: string }): Promise<any[]> => {
    const r = await axiosInstance.get(API_ENDPOINTS.BIRDS.WEIGHT_SUBMISSIONS, { params });
    return r.data.data;
  },
  get: async (id: number): Promise<any> => {
    const r = await axiosInstance.get(API_ENDPOINTS.BIRDS.WEIGHT_SUBMISSION(id));
    return r.data.data;
  },
  extract: async (id: number): Promise<any[]> => {
    const r = await axiosInstance.post(API_ENDPOINTS.BIRDS.WEIGHT_SUBMISSION_EXTRACT(id));
    return r.data.data;
  },
  updateImage: async (
    submissionId: number,
    imageId: number,
    correctedWeightG: number
  ): Promise<any> => {
    const r = await axiosInstance.patch(
      API_ENDPOINTS.BIRDS.WEIGHT_SUBMISSION_IMAGE(submissionId, imageId),
      { corrected_weight_g: correctedWeightG }
    );
    return r.data.data;
  },
  reanalyseImage: async (
    submissionId: number,
    imageId: number,
    correctedWeightG?: number
  ): Promise<any> => {
    const r = await axiosInstance.post(
      API_ENDPOINTS.BIRDS.WEIGHT_SUBMISSION_IMAGE_REANALYSE(submissionId, imageId),
      correctedWeightG !== undefined ? { corrected_weight_g: correctedWeightG } : {}
    );
    return r.data.data;
  },
  updateWeek: async (id: number, week: number): Promise<void> => {
    await axiosInstance.patch(API_ENDPOINTS.BIRDS.WEIGHT_SUBMISSION(id), { week });
  },
  record: async (id: number, group: 'main' | 'isolated'): Promise<any> => {
    const r = await axiosInstance.post(API_ENDPOINTS.BIRDS.WEIGHT_SUBMISSION_RECORD(id), { group });
    return r.data.data;
  },
  delete: async (id: number): Promise<void> => {
    await axiosInstance.delete(API_ENDPOINTS.BIRDS.WEIGHT_SUBMISSION(id));
  },
};
